import Sugar from 'sugar';
import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { repeat } from 'lit/directives/repeat.js';

import { nsSharedStyles, nsLayoutStyles } from '../ns-styles/index.js';

interface Item {
  id: string;
  iconPath?: string;
  title: string;
  description: string;
  disabled?: boolean;
}
@customElement('ns-tiles-options')
export class NSTilesOptions extends LitElement {
  @state()
  private __selectedItem?: string;

  @state()
  private __items?: Item[];

  @state()
  private __groupLabel?: string;

  static styles = [
    nsLayoutStyles,
    nsSharedStyles,
    css`
      button {
        font-size: var(--wa-font-size-s);
        font-weight: 500;
      }

      :host {
        display: block;
      }

      .dynamic-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--wa-space-m);
      }

      .grid-item {
        position: relative;
        background-color: var(--primary-card-background-color);
        border: 2px solid var(--primary-background-color);
        color: var(--primary-card-text-color);
        padding: var(--wa-space-m);
        border-radius: var(--wa-border-radius-l);

        justify-content: safe center;
        min-height: 150px;
      }

      .grid-item.selected {
        background: color-mix(in srgb, var(--ns-theme-green) 20%, transparent);
        border-color: var(--ns-theme-green);
      }

      .grid-item .tick-icon {
        display: none;
        position: absolute;
        top: 8px;
        right: 8px;
        width: 18px;
        height: 18px;
      }

      .grid-item.selected .tick-icon {
        display: block;
      }

      .grid-item:last-child:nth-child(odd) {
        grid-column: 1 / span 2;
        flex-direction: row;
        align-items: center;
        justify-content: center;
      }

      .grid-item:last-child:nth-child(odd) .icon {
        margin-right: var(--wa-space-xs);
      }

      button[disabled] {
        background-color: var(--ns-color-disabled-fill);
        color: var(--ns-color-disabled-text);
        cursor: not-allowed;
      }
    `,
  ];

  public init(items: Item[], label: string) {
    this.__items = items;
    this.__groupLabel = label;
  }

  private __selectTile(id: string) {
    if (!this.__items || this.__items.length < 1) throw new Error('Missing required items');

    const selectedItem = this.renderRoot.querySelector(`button[data-id='${id}']`);
    if (!selectedItem) throw new Error(`Could not find a button item with data-id: ${id}`);

    const gridItems = this.renderRoot.querySelectorAll('.grid-item');
    gridItems.forEach((item) => {
      item.classList.remove('selected');
    });

    selectedItem.classList.add('selected');
    this.__selectedItem = Sugar.String.underscore(id).toUpperCase();
  }

  /** Tells the parent which tile is chosen, from the parent's own Confirm button. The guard for it is the parent's. */
  confirmSelection() {
    this.dispatchEvent(
      new CustomEvent('selected', {
        detail: this.__selectedItem,
        bubbles: true,
        composed: true,
      }),
    );
  }

  render() {
    return html`
      <div class="dynamic-grid" role="group" aria-label="${this.__groupLabel}">
        ${this.__items
          ? repeat(this.__items, (item) => {
              return html`
                <button
                  type="button"
                  data-id="${item.id}"
                  class="grid-item layout vertical clickable"
                  ?disabled=${item.disabled}
                  @click=${() => this.__selectTile(item.id)}
                  aria-pressed=${Sugar.String.underscore(item.id).toUpperCase() === this.__selectedItem
                    ? 'true'
                    : 'false'}
                >
                  <img class="tick-icon" alt="" aria-hidden="true" src="/images/icons/checkmark.svg" />
                  ${when(
                    item.iconPath,
                    () => html`
                      <div class="icon" aria-hidden="true">
                        <img alt="" src="${item.iconPath}" />
                      </div>
                    `,
                    () => html`
                      <svg
                        class="icon"
                        aria-hidden="true"
                        focusable="false"
                        xmlns="http://www.w3.org/2000/svg"
                        enable-background="new 0 0 24 24"
                        height="24px"
                        viewBox="0 0 24 24"
                        width="24px"
                        fill="#5b1bf5"
                      >
                        <g><rect fill="none" height="24" width="24" /></g>
                        <g>
                          <g>
                            <path d="M15,17h2v-3h1v-2l-1-5H2l-1,5v2h1v6h9v-6h4V17z M9,18H4v-4h5V18z" />
                            <rect height="2" width="15" x="2" y="4" />
                            <polygon points="20,18 20,15 18,15 18,18 15,18 15,20 18,20 18,23 20,23 20,20 23,20 23,18" />
                          </g>
                        </g>
                      </svg>
                    `,
                  )}
                  <div class="text-wrapper">
                    <div class="title"><strong>${item.title}</strong></div>
                    <div class="description">${item.description}</div>
                  </div>
                </button>
              `;
            })
          : undefined}
      </div>
    `;
  }
}
